import { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send } from "lucide-react";
import { api } from "@/lib/api";
import { useWhatsAppLink } from "@/lib/whatsapp";

const STORE_KEY = "wafa.chat.session.v1";
const POLL_MS = 2500;

function getStored() { try { return JSON.parse(localStorage.getItem(STORE_KEY) || "null"); } catch { return null; } }
function setStored(v) { localStorage.setItem(STORE_KEY, JSON.stringify(v)); }
function clearStored() { localStorage.removeItem(STORE_KEY); }

export function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [officeStatus, setOfficeStatus] = useState({ online: true, message: "Chat with our team." });
  const [session, setSession] = useState(() => getStored());
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [firstMsg, setFirstMsg] = useState("");
  const [err, setErr] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  // `since` cursor for polling — kept in a ref so that both the poll effect
  // and the send() handler can advance it in-place, avoiding the classic
  // "poll returns the message we just sent because sinceRef was stale"
  // duplication bug.
  const sinceRef = useRef(null);
  // Ids we have already rendered — used to de-dupe when the poll returns a
  // message the send() handler already appended.
  const seenIdsRef = useRef(new Set());

  const appendMessages = (incoming) => {
    if (!incoming || !incoming.length) return;
    // Filter through the "seen" set OUTSIDE the setState updater so that
    // React 18 StrictMode's double-invocation of updaters does not add
    // ids on the first pass and then discard everything as "already seen"
    // on the second pass. Keeping the updater a pure function of `prev`
    // is what makes StrictMode safe.
    const fresh = [];
    for (const m of incoming) {
      if (!m?.id) continue;
      if (seenIdsRef.current.has(m.id)) continue;
      seenIdsRef.current.add(m.id);
      fresh.push(m);
    }
    if (!fresh.length) return;
    setMessages((prev) => [...prev, ...fresh]);
    const last = fresh[fresh.length - 1];
    if (last?.created_at && (!sinceRef.current || last.created_at > sinceRef.current)) {
      sinceRef.current = last.created_at;
    }
  };

  // Fetch office status.
  useEffect(() => {
    api.get("/chat/office-status").then(({ data }) => setOfficeStatus(data)).catch(() => {});
  }, []);

  // Reset in-memory de-dup state whenever the session changes (new chat,
  // or end-chat back to the intro form).
  useEffect(() => {
    seenIdsRef.current = new Set();
    sinceRef.current = null;
    setMessages([]);
  }, [session?.chat_id]);

  // Poll for messages while a session exists. Runs regardless of panel
  // open state so agent replies keep flowing; the initial fetch primes the
  // thread, subsequent ticks only ask for messages newer than sinceRef.
  useEffect(() => {
    if (!session?.chat_id) return;
    let cancel = false;

    const fetchOnce = async () => {
      try {
        const { data } = await api.get(`/chat/${session.chat_id}/messages`, {
          params: sinceRef.current ? { since: sinceRef.current } : {},
        });
        if (cancel) return;
        appendMessages(data.items || []);
      } catch (_) {}
    };

    fetchOnce();
    const id = setInterval(fetchOnce, POLL_MS);
    return () => { cancel = true; clearInterval(id); };
  }, [session?.chat_id]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  const start = async (e) => {
    e.preventDefault();
    setErr("");
    if (!name.trim()) return setErr("Please enter your name.");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return setErr("Please enter a valid email.");
    if (!firstMsg.trim()) return setErr("Please write your first message.");
    setSending(true);
    try {
      const { data } = await api.post("/chat/start", {
        name: name.trim(), email: email.trim().toLowerCase(),
        phone: phone.trim() || null, first_message: firstMsg.trim(),
      });
      const sess = { chat_id: data.chat_id, name: name.trim(), email: email.trim().toLowerCase() };
      setSession(sess); setStored(sess);
      setFirstMsg("");
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Could not start chat.");
    } finally { setSending(false); }
  };

  const send = async (e) => {
    e.preventDefault();
    const body = input.trim();
    if (!body || !session?.chat_id) return;
    setInput("");
    try {
      const { data } = await api.post(`/chat/${session.chat_id}/message`, { body });
      // Use the server-issued id + timestamp so the very next poll (which
      // asks for messages newer than sinceRef) does not return this one
      // and produce a duplicate bubble.
      appendMessages([
        { id: data.id, sender: "visitor", body, created_at: data.created_at },
      ]);
    } catch (_) { setInput(body); }
  };

  const endChat = () => { clearStored(); setSession(null); };
  const wa = useWhatsAppLink("chat_widget");

  return (
    <>
      {!open ? (
        <button
          onClick={() => setOpen(true)}
          data-testid="chat-widget-open"
          className="fab-chat inline-flex h-14 w-14 items-center justify-center rounded-full bg-wafa-deep text-white shadow-lg ring-2 ring-white/80 transition-transform hover:scale-105"
          aria-label="Open chat"
        >
          <MessageCircle className="h-6 w-6" />
        </button>
      ) : (
        <div
          data-testid="chat-widget-panel"
          className="fixed bottom-20 end-4 z-40 flex h-[520px] w-[340px] flex-col rounded-lg border border-wafa-deep/15 bg-white shadow-2xl md:bottom-6 md:end-6 md:w-[360px]"
        >
          <div className="flex items-center justify-between rounded-t-lg bg-wafa-deep px-4 py-3 text-white">
            <div>
              <div className="text-sm font-semibold">Wafa Solutions</div>
              <div className="mt-0.5 flex items-center gap-1.5 text-[10px] uppercase tracking-wider">
                <span className={`inline-block h-1.5 w-1.5 rounded-full ${officeStatus.online ? "bg-wafa-sunshine" : "bg-white/40"}`} />
                {officeStatus.online ? "Online" : "Offline — leave a message"}
              </div>
            </div>
            <button onClick={() => setOpen(false)} aria-label="Close" data-testid="chat-widget-close" className="rounded-full p-1 hover:bg-white/10">
              <X className="h-4 w-4" />
            </button>
          </div>

          {!session ? (
            <form onSubmit={start} className="flex-1 space-y-3 overflow-y-auto p-4" data-testid="chat-widget-form-start">
              <p className="text-xs text-wafa-ink/70">{officeStatus.message}</p>
              <input placeholder="Your name" value={name} onChange={(e) => setName(e.target.value)} data-testid="chat-name" className="h-10 w-full rounded-md border border-wafa-deep/15 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-wafa-primary" />
              <input type="email" placeholder="Your email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="chat-email" className="h-10 w-full rounded-md border border-wafa-deep/15 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-wafa-primary" />
              <input placeholder="Phone (optional)" value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="chat-phone" className="h-10 w-full rounded-md border border-wafa-deep/15 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-wafa-primary" />
              <textarea placeholder="How can we help?" rows={3} value={firstMsg} onChange={(e) => setFirstMsg(e.target.value)} data-testid="chat-first-message" className="w-full rounded-md border border-wafa-deep/15 p-3 text-sm focus:outline-none focus:ring-2 focus:ring-wafa-primary" />
              {err ? <div className="text-xs text-wafa-tomato" data-testid="chat-error">{err}</div> : null}
              <button type="submit" disabled={sending} data-testid="chat-start-submit" className="w-full rounded-md bg-wafa-primary py-2.5 text-sm font-semibold text-white hover:bg-wafa-deep disabled:opacity-60">
                {sending ? "Starting…" : "Start chat"}
              </button>
              {wa ? (
                <a href={wa.href} onClick={wa.onClick} target="_blank" rel="noopener noreferrer" data-testid="chat-widget-wa" className="block text-center text-xs text-wafa-primary hover:text-wafa-deep">Chat on WhatsApp instead</a>
              ) : null}
            </form>
          ) : (
            <>
              <div ref={scrollRef} className="flex-1 space-y-2 overflow-y-auto bg-wafa-cream/30 p-3" data-testid="chat-widget-thread">
                {messages.map((m) => (
                  <div key={m.id} className={`flex ${m.sender === "visitor" ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${m.sender === "visitor" ? "bg-wafa-primary text-white" : m.sender === "agent" ? "bg-white border border-wafa-deep/10 text-wafa-deep" : "bg-wafa-sunshine/40 text-wafa-deep text-xs"}`}>
                      {m.body}
                      <div className="mt-1 text-[9px] opacity-70">{new Date(m.created_at).toLocaleTimeString()}</div>
                    </div>
                  </div>
                ))}
              </div>
              <form onSubmit={send} className="flex items-center gap-2 border-t border-wafa-deep/10 p-2" data-testid="chat-widget-form-send">
                <input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Type a message…" data-testid="chat-input" className="h-10 flex-1 rounded-md border border-wafa-deep/15 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-wafa-primary" />
                <button type="submit" data-testid="chat-send" className="inline-flex h-10 w-10 items-center justify-center rounded-md bg-wafa-primary text-white hover:bg-wafa-deep">
                  <Send className="h-4 w-4" />
                </button>
              </form>
              <div className="flex items-center justify-between border-t border-wafa-deep/10 px-3 py-1.5 text-[10px] text-wafa-ink/60">
                <button onClick={endChat} data-testid="chat-end" className="hover:text-wafa-deep">End chat</button>
                {wa ? <a href={wa.href} onClick={wa.onClick} target="_blank" rel="noopener noreferrer" data-testid="chat-widget-wa-thread" className="text-wafa-primary hover:text-wafa-deep">Chat on WhatsApp</a> : null}
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
}
